import type { CalculationRulesetOptionsQuery } from "@backchannel/tax-contracts";
import type {
  AcquisitionSettingsUpdate,
  AutoApprovalSettings,
  AutoApprovalSettingsUpdate,
  BrowserAgentCredentialsStatus,
  BrowserAgentCredentialsUpdate,
  BrowserAgentDemoCreate,
  BrowserAgentFinding,
  BrowserAgentSettings,
  BrowserAgentSettingsUpdate,
  BrowserAgentTask,
  BrowserAgentTaskAction,
  BrowserAgentTaskCreate,
  FactProposalRequest,
  FiscalFactGroupConfirm,
  FiscalFactQueueTab,
  FiscalFactReviewGroupDetail,
  FiscalFactReviewGroupQuery,
  FiscalFactReviewSession,
  FiscalFactReviewSessionAction,
  FiscalFactReviewSessionStart,
  FiscalFactScopeRestriction,
  FiscalFactVerification,
  FiscalFactsSettings,
  FiscalFactsSettingsUpdate,
  FiscalTask,
  IpiRestatementRequest,
  NfeAcknowledgementRequest,
  NfeAnalysisRequest,
  NfeDirection,
  NfeFindingQuery,
  NfeImportRequest,
  NfeMonthPathQuery,
  NfeObservedPathQuery,
  NfeValidationRequest,
  NfeWorkspaceSettingsUpdate,
  ResearchSweep,
  ResearchSweepAction,
  ResearchSweepCreate,
  ResearchSweepPreviewRequest,
  ResearchSweepStatus,
  ResearchUnitKind,
  ResearchUnitStatus,
  RuleProposalDraftOrigin,
  RuleProposalDraftStatus,
  RuleProposalDraftSubmit,
  SessionActivityQuery,
  SessionReplayLookupQuery,
  SessionReplayQuery,
  SessionReplaySettingsUpdate,
  StAgreement,
  StAgreementRetireMisidentified,
  StAgreementVerification,
  TaxPolicyVersionId,
  UploadFailureDetail,
  UploadFailureQuery,
  UploadFailureReviewRequest,
  WebResearchSettings,
  WebResearchSettingsUpdate,
} from "@backchannel/tax-contracts";
import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useRef, useState } from "react";
import {
  BROWSER_AGENT_RUN_POLL_MS,
  BROWSER_AGENT_STEP_POLL_MS,
  type StepLog,
  mergeSteps,
  sniffImageType,
} from "~/lib/domain/browser-agent";
import { calculationJobsPollInterval } from "~/lib/domain/calculation-jobs";
import { artifactWindow, locateInPage } from "~/lib/domain/fiscal-review";
import { isLiveSweep } from "~/lib/domain/web-research";
import { useApi } from "./api-context";
import { DEFAULT_PAGE_SIZE, type TaxApiClient } from "./client";
import { TaxApiError } from "./errors";
import { exactRecord, recordNotFound } from "./record-lookup";
import type {
  AuditQuery,
  BrowserAgentFindingQuery,
  BrowserAgentRunQuery,
  BrowserAgentTaskItemQuery,
  CalculationJobQuery,
  CalculationQuery,
  CoverageGapQuery,
  CreateRuleProposalRequest,
  DlqReplayRequest,
  EvidenceBulkRequest,
  EvidenceDecision,
  EvidenceQuery,
  EvidenceTaskCreate,
  EvidenceTaskPatch,
  EvidenceTaskQuery,
  FiscalResearchTaskCreate,
  FiscalResearchTaskQuery,
  FiscalTaskQuery,
  FiscalTaskResultQuery,
  IntegrationRequestQuery,
  LinkRunRequest,
  NcmCatalogQuery,
  PrincipalAccessWrite,
  ProposalQuery,
  RuleQuery,
  RulesetQuery,
  SourceRun,
  SourceRunEvidenceQuery,
} from "./types";

export const SOURCE_RUN_IN_FLIGHT_STATUSES = [
  "QUEUED",
  "DISCOVERING",
  "FETCHING",
  "EXTRACTING",
] as const;

export const SOURCE_RUN_POLL_INTERVAL_MS = 2_000;
/** First poll delay; it grows by FISCAL_TASK_POLL_GROWTH up to FISCAL_TASK_POLL_MAX_MS. */
export const FISCAL_TASK_POLL_INTERVAL_MS = 1_500;
export const FISCAL_TASK_POLL_MAX_MS = 5_000;
const FISCAL_TASK_POLL_GROWTH = 1.25;

export const FISCAL_TASK_IN_FLIGHT_STATUSES = ["QUEUED", "RUNNING"] as const;

export function isFiscalTaskInFlight(status: string | undefined): boolean {
  return status === "QUEUED" || status === "RUNNING";
}

/** A failed poll slows the refresh down; it never ends it while the task may still be running. */
export const FISCAL_TASK_POLL_BACKOFF_MS = FISCAL_TASK_POLL_MAX_MS;

/**
 * In flight (or not yet read): refresh every 1.5 s, easing towards 5 s as the
 * task keeps running (`polls` is how many reads succeeded so far). Terminal:
 * stop. A transient failure (network, 5xx, a timeout) keeps polling at the
 * slowest pace: stopping on the first one left the screen on "Queued" until a
 * manual reload even though the calculation had finished on the server. Only a
 * refusal that will not change by itself (403, 404 and the like) ends the
 * polling.
 */
export function fiscalTaskPollInterval(input: {
  status?: string;
  error?: unknown;
  polls?: number;
}): number | false {
  if (input.error) {
    const terminal = input.error instanceof TaxApiError && !input.error.retryable;
    if (terminal || (input.status !== undefined && !isFiscalTaskInFlight(input.status))) {
      return false;
    }
    return FISCAL_TASK_POLL_BACKOFF_MS;
  }
  if (input.status !== undefined && !isFiscalTaskInFlight(input.status)) return false;
  const polls = Math.max(0, input.polls ?? 0);
  return Math.min(
    FISCAL_TASK_POLL_MAX_MS,
    Math.round(FISCAL_TASK_POLL_INTERVAL_MS * FISCAL_TASK_POLL_GROWTH ** polls),
  );
}

/**
 * Options every live read of a fiscal task shares. Polling must also run while
 * the window is unfocused or the tab hidden (react-query pauses it by default),
 * and the app turns focus refetching off, so a task that finished in the
 * background shows as soon as the person comes back.
 */
export const FISCAL_TASK_LIVE_OPTIONS = {
  staleTime: 0,
  refetchOnWindowFocus: "always",
  refetchIntervalInBackground: true,
} as const;

export function isSourceRunInFlight(
  status: string | undefined,
): status is (typeof SOURCE_RUN_IN_FLIGHT_STATUSES)[number] {
  return (
    status === "QUEUED" ||
    status === "DISCOVERING" ||
    status === "FETCHING" ||
    status === "EXTRACTING"
  );
}

/** Bound polling: in-flight statuses refresh; terminal and errors stop. */
export function sourceRunPollInterval(input: {
  status?: string;
  error?: unknown;
}): number | false {
  if (input.error) return false;
  if (input.status === undefined) return SOURCE_RUN_POLL_INTERVAL_MS;
  return isSourceRunInFlight(input.status) ? SOURCE_RUN_POLL_INTERVAL_MS : false;
}

/**
 * Query keys are structured so a filter change invalidates exactly one list
 * and a mutation can invalidate a whole domain without touching the others.
 */
export const queryKeys = {
  acquisitionSettings: (organizationId: string) =>
    ["acquisition-settings", organizationId] as const,
  session: ["session"] as const,
  principals: ["principals"] as const,
  passkeys: ["passkeys"] as const,
  integrationKeys: {
    all: ["integration-keys"] as const,
    list: (organizationId?: string) => ["integration-keys", "list", organizationId] as const,
  },
  mcpTools: {
    all: ["mcp-tools"] as const,
    list: ["mcp-tools", "list"] as const,
  },
  integrationRequests: {
    all: ["integration-requests"] as const,
    list: (query: IntegrationRequestQuery, organizationId?: string) =>
      ["integration-requests", "list", organizationId, query] as const,
  },
  overview: ["overview"] as const,
  fiscalFacts: {
    all: ["fiscal-facts"] as const,
    queue: (tab: string, cursor: string | null) => ["fiscal-facts", "queue", tab, cursor] as const,
    list: (query: Record<string, unknown>) => ["fiscal-facts", "list", query] as const,
    detail: (factId: string) => ["fiscal-facts", "detail", factId] as const,
    settings: ["fiscal-facts", "settings"] as const,
    shadowSummary: (days: number) => ["fiscal-facts", "shadow-summary", days] as const,
    reviewGroups: (query: Record<string, unknown>) =>
      ["fiscal-facts", "review-groups", query] as const,
    reviewGroup: (groupId: string) => ["fiscal-facts", "review-group", groupId] as const,
    confirmedReviews: (cursor: string | null) =>
      ["fiscal-facts", "confirmed-reviews", cursor] as const,
    agreements: (query: Record<string, unknown>) =>
      ["fiscal-facts", "st-agreements", query] as const,
    agreement: (agreementId: string) => ["fiscal-facts", "st-agreement", agreementId] as const,
    misidentified: ["fiscal-facts", "st-agreements", "misidentified"] as const,
  },
  nfe: {
    all: ["nfe"] as const,
    imports: (cursor: string | null) => ["nfe", "imports", cursor] as const,
    import: (batchId: string) => ["nfe", "import", batchId] as const,
    paths: (query: Record<string, unknown>) => ["nfe", "paths", query] as const,
    path: (pathId: string) => ["nfe", "path", pathId] as const,
    /** Everything of the tax manager workspace sits under `["nfe", "workspace"]`. */
    workspace: ["nfe", "workspace"] as const,
    tree: (direction: string | undefined) =>
      ["nfe", "workspace", "tree", direction ?? "ALL"] as const,
    period: (period: string, direction: string | undefined) =>
      ["nfe", "workspace", "period", period, direction ?? "ALL"] as const,
    monthPaths: (period: string, query: Record<string, unknown>) =>
      ["nfe", "workspace", "paths", period, query] as const,
    monthPath: (period: string, pathId: string, direction: string | undefined) =>
      ["nfe", "workspace", "path", period, pathId, direction ?? "ALL"] as const,
    findings: (period: string, query: Record<string, unknown>) =>
      ["nfe", "workspace", "findings", period, query] as const,
    events: (period: string) => ["nfe", "workspace", "events", period] as const,
    runs: (period: string) => ["nfe", "workspace", "runs", period] as const,
    settings: ["nfe", "workspace", "settings"] as const,
  },
  webResearch: {
    all: ["web-research"] as const,
    sweeps: ["web-research", "sweeps"] as const,
    sweep: (sweepId: string) => ["web-research", "sweep", sweepId] as const,
    units: (sweepId: string, query: Record<string, unknown>) =>
      ["web-research", "units", sweepId, query] as const,
    settings: ["web-research", "settings"] as const,
  },
  autoApproval: {
    all: ["auto-approval"] as const,
    settings: ["auto-approval", "settings"] as const,
    package: ["auto-approval", "package"] as const,
    preview: ["auto-approval", "preview"] as const,
    runs: ["auto-approval", "runs"] as const,
    report: (date: string) => ["auto-approval", "report", date] as const,
  },
  browserAgent: {
    all: ["browser-agent"] as const,
    runs: (query: BrowserAgentRunQuery) => ["browser-agent", "runs", query] as const,
    run: (runId: string) => ["browser-agent", "run", runId] as const,
    presence: ["browser-agent", "presence"] as const,
    steps: (runId: string) => ["browser-agent", "steps", runId] as const,
    findings: (query: BrowserAgentFindingQuery) => ["browser-agent", "findings", query] as const,
    settings: ["browser-agent", "settings"] as const,
    credentials: ["browser-agent", "credentials"] as const,
    tasks: ["browser-agent", "tasks"] as const,
    task: (taskId: string) => ["browser-agent", "task", taskId] as const,
    taskItems: (taskId: string, query: BrowserAgentTaskItemQuery) =>
      ["browser-agent", "task-items", taskId, query] as const,
    lookupJobs: ["browser-agent", "lookup-jobs"] as const,
    recordings: (runId: string) => ["browser-agent", "recordings", runId] as const,
    recordingUrl: (recordingId: string, demoId: string | undefined) =>
      ["browser-agent", "recording-url", recordingId, demoId ?? ""] as const,
    demos: ["browser-agent", "demos"] as const,
    findingDaily: (from: string, to: string) =>
      ["browser-agent", "finding-daily", from, to] as const,
    dayFindings: (day: string) => ["browser-agent", "day-findings", day] as const,
    demo: (demoId: string) => ["browser-agent", "demo", demoId] as const,
    frame: (key: string, generation: string) =>
      ["browser-agent", "frame", key, generation] as const,
  },
  evidence: {
    all: ["evidence"] as const,
    list: (query: EvidenceQuery) => ["evidence", "list", query] as const,
    detail: (id: string) => ["evidence", "detail", id] as const,
    artifact: (id: string, offset: number, locatorOffset = 0) =>
      ["evidence", "artifact", id, offset, locatorOffset] as const,
  },
  evidenceTasks: {
    all: ["evidence-tasks"] as const,
    list: (query: EvidenceTaskQuery) => ["evidence-tasks", "list", query] as const,
    detail: (id: string) => ["evidence-tasks", "detail", id] as const,
    assignees: ["evidence-tasks", "assignees"] as const,
  },
  proposals: {
    all: ["proposals"] as const,
    list: (query: ProposalQuery) => ["proposals", "list", query] as const,
    detail: (id: string) => ["proposals", "detail", id] as const,
    publishedMatches: (id: string) => ["proposals", "published-matches", id] as const,
  },
  ruleDrafts: {
    all: ["rule-proposal-drafts"] as const,
    list: (query: Record<string, unknown>) => ["rule-proposal-drafts", "list", query] as const,
    readiness: (rulesetId: string, policy: string) =>
      ["rule-proposal-drafts", "readiness", rulesetId, policy] as const,
  },
  calculationRulesets: {
    all: ["calculation-ruleset-options"] as const,
    list: (query: CalculationRulesetOptionsQuery) =>
      ["calculation-ruleset-options", "list", query] as const,
  },
  rulesets: {
    all: ["rulesets"] as const,
    list: (query: RulesetQuery) => ["rulesets", "list", query] as const,
    detail: (id: string) => ["rulesets", "detail", id] as const,
    diff: (from: string, to: string) => ["rulesets", "diff", from, to] as const,
  },
  rules: {
    all: ["rules"] as const,
    list: (query: RuleQuery) => ["rules", "list", query] as const,
    detail: (id: string, rulesetVersion?: string) =>
      ["rules", "detail", id, rulesetVersion] as const,
  },
  ncmCatalog: {
    all: ["ncm-catalog"] as const,
    list: (query: NcmCatalogQuery) => ["ncm-catalog", "list", query] as const,
    illustration: (chapter: string) => ["ncm-catalog", "illustration", chapter] as const,
  },
  cestCatalog: {
    all: ["cest-catalog"] as const,
    list: (query: { cest?: string; q?: string; limit?: number }) =>
      ["cest-catalog", "list", query] as const,
  },
  calculations: {
    all: ["calculations"] as const,
    list: (query: CalculationQuery) => ["calculations", "list", query] as const,
    detail: (id: string) => ["calculations", "detail", id] as const,
    upload: (id: string) => ["calculations", "upload", id] as const,
    explain: (calculationId: string, itemId: string) =>
      ["calculations", "explain", calculationId, itemId] as const,
  },
  infraCost: ["infra-cost"] as const,
  uploadFailures: {
    all: ["upload-failures"] as const,
    list: (query: UploadFailureQuery) => ["upload-failures", "list", query] as const,
    detail: (failureId: string) => ["upload-failures", "detail", failureId] as const,
  },
  sessionReplays: {
    all: ["session-replays"] as const,
    list: (query: SessionReplayQuery) => ["session-replays", "list", query] as const,
    detail: (id: string) => ["session-replays", "detail", id] as const,
    events: (id: string) => ["session-replays", "events", id] as const,
    activity: (id: string) => ["session-replays", "activity", id] as const,
  },
  sessionActivity: {
    all: ["session-activity"] as const,
    list: (query: SessionActivityQuery) => ["session-activity", "list", query] as const,
  },
  sessionReplayLookup: (query: SessionReplayLookupQuery) =>
    ["session-replay-lookup", query] as const,
  sessionReplaySettings: ["session-replay-settings"] as const,
  calculationJobs: {
    all: ["calculation-jobs"] as const,
    list: (query: CalculationJobQuery) => ["calculation-jobs", "list", query] as const,
  },
  fiscalTasks: {
    all: ["fiscal-tasks"] as const,
    list: (query: FiscalTaskQuery) => ["fiscal-tasks", "list", query] as const,
    detail: (id: string) => ["fiscal-tasks", "detail", id] as const,
    results: (id: string, query: FiscalTaskResultQuery) =>
      ["fiscal-tasks", "results", id, query] as const,
  },
  coverageGaps: {
    all: ["coverage-gaps"] as const,
    list: (query: CoverageGapQuery) => ["coverage-gaps", "list", query] as const,
  },
  fiscalResearchTasks: {
    all: ["fiscal-research-tasks"] as const,
    list: (query: FiscalResearchTaskQuery) => ["fiscal-research-tasks", "list", query] as const,
    detail: (id: string) => ["fiscal-research-tasks", "detail", id] as const,
  },
  sources: {
    all: ["sources"] as const,
    list: ["sources", "list"] as const,
    detail: (id: string) => ["sources", "detail", id] as const,
  },
  sourceRuns: {
    all: ["source-runs"] as const,
    detail: (id: string) => ["source-runs", "detail", id] as const,
    evidenceRoot: (id: string) => ["source-runs", "evidence", id] as const,
    evidence: (id: string, query: SourceRunEvidenceQuery) =>
      ["source-runs", "evidence", id, query] as const,
  },
  audit: {
    all: ["audit"] as const,
    list: (query: AuditQuery) => ["audit", "list", query] as const,
    detail: (id: string) => ["audit", "detail", id] as const,
  },
};

export function useSessionQuery(options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.session,
    queryFn: ({ signal }) => api.getSession(signal),
    staleTime: 5 * 60_000,
    retry: false,
    enabled: options.enabled ?? true,
  });
}

export function usePasskeysQuery(options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.passkeys,
    queryFn: ({ signal }) => api.listPasskeys(signal),
    staleTime: 15_000,
    retry: false,
    enabled: options.enabled ?? true,
  });
}

export function usePasskeyMutations() {
  const api = useApi();
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.passkeys });
  };
  return {
    registerOptions: useMutation({
      mutationFn: () => api.createPasskeyRegistrationOptions(),
    }),
    registerVerify: useMutation({
      mutationFn: (input: unknown) => api.verifyPasskeyRegistration(input),
      onSuccess: invalidate,
    }),
    remove: useMutation({
      mutationFn: (passkeyId: string) => api.deletePasskey(passkeyId),
      onSuccess: invalidate,
    }),
  };
}

export function usePrincipalAccessQuery(options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.principals,
    queryFn: ({ signal }) => api.listPrincipalAccess(signal),
    staleTime: 15_000,
    retry: false,
    enabled: options.enabled ?? true,
  });
}

export function usePrincipalAccessMutations() {
  const api = useApi();
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.principals });
    void queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
  };
  return {
    create: useMutation({
      mutationFn: (input: PrincipalAccessWrite) => api.createPrincipalAccess(input),
      // An email failure can leave a persisted pending user; expose it for resend.
      onSettled: invalidate,
    }),
    update: useMutation({
      mutationFn: (input: { id: string; access: PrincipalAccessWrite }) =>
        api.updatePrincipalAccess(input),
      onSuccess: invalidate,
    }),
    resend: useMutation({
      mutationFn: (id: string) => api.resendPrincipalInvite(id),
      onSuccess: invalidate,
    }),
  };
}

export function useMcpToolsQuery(options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.mcpTools.list,
    queryFn: ({ signal }) => api.listMcpTools(signal),
    staleTime: 60_000,
    retry: false,
    enabled: options.enabled ?? true,
  });
}

export function useIntegrationKeysQuery(
  options: { enabled?: boolean; organizationId?: string } = {},
) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.integrationKeys.list(options.organizationId),
    queryFn: ({ signal }) => api.listIntegrationKeys(signal, options.organizationId),
    staleTime: 15_000,
    retry: false,
    enabled: options.enabled ?? true,
  });
}

export function useIntegrationRequestsQuery(
  query: IntegrationRequestQuery,
  options: { enabled?: boolean; organizationId?: string } = {},
) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.integrationRequests.list(query, options.organizationId),
    queryFn: ({ signal }) => api.listIntegrationRequests(query, signal, options.organizationId),
    staleTime: 15_000,
    retry: false,
    enabled: options.enabled ?? true,
  });
}

export const OVERVIEW_REFRESH_INTERVAL_MS = 60_000;

export function useOverviewQuery(options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.overview,
    queryFn: ({ signal }) => api.getOverview(signal),
    staleTime: 30_000,
    refetchInterval: OVERVIEW_REFRESH_INTERVAL_MS,
    refetchOnWindowFocus: true,
    enabled: options.enabled ?? true,
  });
}

export function useEvidenceList(query: EvidenceQuery) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.evidence.list(query),
    queryFn: ({ signal }) => api.listEvidence(query, signal),
    // Keeping the previous page mounted stops the table from collapsing to a
    // spinner every time an operator types one more character in the filter.
    placeholderData: (previous) => previous,
    staleTime: 15_000,
  });
}

/**
 * Small, state-scoped read model for the overview map. It deliberately reuses
 * the evidence list contract so selecting a state never introduces a second
 * source of truth or an ungoverned news feed.
 */
export function useEvidencePreviewByJurisdiction(
  jurisdiction: string | null,
  options: { enabled?: boolean } = {},
) {
  const api = useApi();
  const query = {
    jurisdiction: jurisdiction ? [jurisdiction] : [],
    limit: 3,
    sort: "fetchedAt",
    dir: "desc",
  } satisfies EvidenceQuery;

  return useQuery({
    queryKey: queryKeys.evidence.list(query),
    queryFn: ({ signal }) => api.listEvidence(query, signal),
    enabled: Boolean(jurisdiction) && (options.enabled ?? true),
    staleTime: 60_000,
  });
}

export function useEvidenceDetail(evidenceId: string, options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.evidence.detail(evidenceId),
    queryFn: ({ signal }) => api.getEvidence(evidenceId, signal),
    enabled: options.enabled ?? Boolean(evidenceId),
  });
}

export function useEvidenceArtifact(
  evidenceId: string,
  options: {
    offset?: number;
    /** Code-point start (locator unit); replaces the byte `offset`. */
    charOffset?: number;
    limit?: number;
    locatorOffset?: number;
    locatorLimit?: number;
    enabled?: boolean;
  } = {},
) {
  const api = useApi();
  const offset = options.offset ?? 0;
  const locatorOffset = options.locatorOffset ?? 0;
  return useQuery({
    queryKey: [
      ...queryKeys.evidence.artifact(evidenceId, offset, locatorOffset),
      options.charOffset ?? null,
      options.limit ?? null,
    ],
    queryFn: ({ signal }) =>
      api.getEvidenceArtifact(
        evidenceId,
        {
          kind: "normalized",
          ...(options.charOffset === undefined ? { offset } : { charOffset: options.charOffset }),
          limit: options.limit,
          locatorOffset,
          locatorLimit: options.locatorLimit,
        },
        signal,
      ),
    enabled: Boolean(evidenceId) && (options.enabled ?? true),
    staleTime: 60_000,
  });
}

export type EvidenceContext =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "located"; before: string; after: string; exact: boolean; contentSha256: string }
  | { status: "notFound"; contentSha256: string }
  | { status: "unavailable"; reason: "missing" | "forbidden" | "error" };

/**
 * The preserved text around a quote's code-point locator: fetches the page
 * that starts `pad` code points before it (`charOffset`, never a byte offset:
 * accented text has more bytes than characters) and finds the quote nearest
 * the locator. `notFound` means the stored text does not hold the quote there
 * (show it as a discrepancy, never as a highlight).
 */
export function useEvidenceContext(input: {
  evidenceId: string | null;
  locator: { textStart: number; textEnd: number } | null;
  quote: string | null;
  enabled: boolean;
  pad?: number;
}): EvidenceContext {
  const pad = input.pad ?? 480;
  const window = input.locator ? artifactWindow(input.locator, pad) : { charOffset: 0, limit: 1 };
  const active = Boolean(input.enabled && input.evidenceId && input.locator && input.quote);
  const query = useEvidenceArtifact(input.evidenceId ?? "", {
    charOffset: window.charOffset,
    limit: window.limit,
    locatorLimit: 1,
    enabled: active,
  });
  if (!input.evidenceId) return { status: "unavailable", reason: "missing" };
  if (!input.enabled) return { status: "unavailable", reason: "forbidden" };
  if (!input.locator || !input.quote) return { status: "idle" };
  if (query.isPending) return { status: "loading" };
  if (query.isError || !query.data) {
    const status = query.error instanceof TaxApiError ? query.error.status : 0;
    return {
      status: "unavailable",
      reason: status === 403 ? "forbidden" : status === 404 ? "missing" : "error",
    };
  }
  const text = query.data.text;
  const found = locateInPage(query.data, input.locator, input.quote);
  if (!found) return { status: "notFound", contentSha256: query.data.contentSha256 };
  const start = found.index;
  const end = found.end;
  return {
    status: "located",
    exact: found.exact,
    before: text.slice(Math.max(0, start - pad), start),
    after: text.slice(end, end + pad),
    contentSha256: query.data.contentSha256,
  };
}

export function useDownloadEvidenceArtifact() {
  const api = useApi();
  return useMutation({
    mutationFn: ({ evidenceId }: { evidenceId: string }) =>
      api.downloadEvidenceArtifact(evidenceId),
  });
}

export function useEvidenceTasks(query: EvidenceTaskQuery = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.evidenceTasks.list(query),
    queryFn: ({ signal }) => api.listEvidenceTasks(query, signal),
    placeholderData: (previous) => previous,
  });
}

export function useEvidenceTask(taskId: string, options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.evidenceTasks.detail(taskId),
    queryFn: ({ signal }) => api.getEvidenceTask(taskId, signal),
    enabled: Boolean(taskId) && (options.enabled ?? true),
  });
}

export function useEvidenceTaskAssignees(options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.evidenceTasks.assignees,
    queryFn: ({ signal }) => api.listEvidenceTaskAssignees(signal),
    staleTime: 60_000,
    enabled: options.enabled ?? true,
  });
}

export function useEvidenceTaskMutations() {
  const api = useApi();
  const queryClient = useQueryClient();
  const invalidate = (taskId?: string) => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.evidenceTasks.all });
    if (taskId) {
      void queryClient.invalidateQueries({ queryKey: queryKeys.evidenceTasks.detail(taskId) });
    }
    void queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
  };
  return {
    create: useMutation({
      mutationFn: (input: { task: EvidenceTaskCreate; idempotencyKey?: string }) =>
        api.createEvidenceTask(input.task, input.idempotencyKey),
      onSuccess: (task) => {
        queryClient.setQueryData(queryKeys.evidenceTasks.detail(task.id), task);
        invalidate(task.id);
      },
    }),
    update: useMutation({
      mutationFn: (input: {
        taskId: string;
        patch: EvidenceTaskPatch;
        revision: number;
        idempotencyKey?: string;
      }) => api.updateEvidenceTask(input),
      onSuccess: (task) => {
        queryClient.setQueryData(queryKeys.evidenceTasks.detail(task.id), task);
        invalidate(task.id);
      },
    }),
  };
}

export function useReviewEvidence() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { evidenceId: string; decision: EvidenceDecision; reason: string }) =>
      api.reviewEvidence(input),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.evidence.all });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.evidence.detail(variables.evidenceId),
      });
      void queryClient.invalidateQueries({ queryKey: queryKeys.overview });
    },
  });
}

export function useBulkReviewEvidence() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: EvidenceBulkRequest) => api.bulkReviewEvidence(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.evidence.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.overview });
      void queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
    },
  });
}

export function useProposalList(query: ProposalQuery) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.proposals.list(query),
    queryFn: ({ signal }) => api.listProposals(query, signal),
    placeholderData: (previous) => previous,
  });
}

export function useProposalDetail(proposalId: string) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.proposals.detail(proposalId),
    queryFn: ({ signal }) => api.getProposal(proposalId, signal),
  });
}

export function usePublishedRuleMatches(proposalId: string) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.proposals.publishedMatches(proposalId),
    queryFn: ({ signal }) => api.getPublishedRuleMatches(proposalId, signal),
    enabled: Boolean(proposalId),
    retry: false,
  });
}

export function useCreateProposal() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateRuleProposalRequest) => api.createProposal(input),
    onSuccess: (proposal) => {
      queryClient.setQueryData(queryKeys.proposals.detail(proposal.id), proposal);
      void queryClient.invalidateQueries({ queryKey: queryKeys.proposals.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.evidence.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.overview });
    },
  });
}

/**
 * Dry run (reads only) and commit of a proposal plan. A commit creates
 * PENDING_REVIEW proposals, so it refreshes the proposal lists and the audit.
 */
export function useImportProposalPlan() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.importProposalPlan>[0]) =>
      api.importProposalPlan(input),
    onSuccess: (result) => {
      if (result.mode !== "COMMIT") return;
      void queryClient.invalidateQueries({ queryKey: queryKeys.proposals.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.overview });
    },
  });
}

/** Exports the active ruleset content (read only); the page downloads the result. */
export function useExportActiveRuleset() {
  const api = useApi();
  return useMutation({ mutationFn: () => api.exportActiveRuleset() });
}

export function useDecideProposal() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      proposalId: string;
      decision: "APPROVED" | "REJECTED";
      reason: string;
    }) => api.decideProposal(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.proposals.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.rulesets.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.overview });
    },
  });
}

/** Read-only calculation selector metadata; never queries governance rulesets. */
export function useCalculationRulesetOptions(
  query: CalculationRulesetOptionsQuery = {},
  options: { enabled?: boolean } = {},
) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.calculationRulesets.list(query),
    queryFn: ({ signal }) => api.listCalculationRulesets(query, signal),
    enabled: options.enabled ?? true,
  });
}

export function useRulesetList(query: RulesetQuery, options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.rulesets.list(query),
    queryFn: ({ signal }) => api.listRulesets(query, signal),
    enabled: options.enabled ?? true,
    placeholderData: (previous) => previous,
  });
}

/** Published ruleset currently in force. Shared by lookup, library and calculator. */
export function useActiveRuleset(options: { enabled?: boolean } = {}) {
  const query = useRulesetList(
    {
      status: ["PUBLISHED"],
      sort: "publishedAt",
      dir: "desc",
      limit: 20,
    },
    options,
  );
  const active = query.data?.items.find((item) => item.isActive) ?? query.data?.items[0] ?? null;
  return { ...query, active };
}

export function useRulesetDetail(rulesetId: string) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.rulesets.detail(rulesetId),
    queryFn: ({ signal }) => api.getRuleset(rulesetId, signal),
  });
}

export function useRulesetDiff(
  from: string | undefined,
  to: string | undefined,
  /** Bind draft review cache to its exact content, not just the mutable version string. */
  reviewContentDigest?: string,
) {
  const api = useApi();
  const key = queryKeys.rulesets.diff(from ?? "", to ?? "");
  return useQuery({
    queryKey: reviewContentDigest ? [...key, "review-content", reviewContentDigest] : key,
    queryFn: ({ signal }) => api.getRulesetDiff({ from: from ?? "", to: to ?? "" }, signal),
    enabled: Boolean(from && to),
  });
}

export function usePublishRuleset() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { rulesetId: string; expectedDigest: string; reason: string }) =>
      api.publishRuleset(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.rulesets.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.proposals.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.overview });
    },
  });
}

export function useSetRulesetPolicy() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: Parameters<typeof api.setRulesetPolicy>[0]) => api.setRulesetPolicy(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.rulesets.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
    },
  });
}

export function useRollbackRuleset() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { targetRulesetId: string; reason: string }) => api.rollbackRuleset(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.rulesets.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
    },
  });
}

export function useRuleList(query: RuleQuery, options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.rules.list(query),
    queryFn: ({ signal }) => api.listRules(query, signal),
    enabled: options.enabled,
    placeholderData: query.matchMode === "route-candidates" ? undefined : (previous) => previous,
  });
}

export function useNcmCatalogSearch(query: NcmCatalogQuery) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.ncmCatalog.list(query),
    queryFn: ({ signal }) => api.listNcmCatalog(query, signal),
    staleTime: 60_000,
  });
}

export function useNcmCatalogLookup(ncm: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.ncmCatalog.list({ ncm, limit: 1 }),
    queryFn: ({ signal }) => api.listNcmCatalog({ ncm, limit: 1 }, signal),
    enabled: Boolean(ncm),
    staleTime: 60_000,
  });
}

export function useCestCatalogLookup(cest: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.cestCatalog.list({ cest, limit: 1 }),
    queryFn: ({ signal }) => api.listCestCatalog({ cest, limit: 1 }, signal),
    enabled: Boolean(cest),
    staleTime: 60_000,
  });
}

export function useChapterIllustration(chapter: string | null) {
  const api = useApi();
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: queryKeys.ncmCatalog.illustration(chapter ?? ""),
    queryFn: ({ signal }) => api.getChapterIllustration(chapter ?? "", signal),
    enabled: Boolean(chapter),
    staleTime: 60_000,
  });
  const ensure = useMutation({
    mutationFn: (nextChapter: string) => api.ensureChapterIllustration(nextChapter),
    onSuccess: (illustration) => {
      queryClient.setQueryData(
        queryKeys.ncmCatalog.illustration(illustration.chapter),
        illustration,
      );
    },
  });
  return { query, ensure };
}

export function useRuleDetail(ruleId: string, rulesetVersion?: string) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.rules.detail(ruleId, rulesetVersion),
    queryFn: async ({ signal }) => {
      const page = await api.listRules({ q: ruleId, rulesetVersion, limit: 50 }, signal);
      const match = exactRecord(page.items, ruleId);
      if (!match) throw recordNotFound("rule");
      return match;
    },
    enabled: Boolean(ruleId),
  });
}

export function useCalculationList(query: CalculationQuery, options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.calculations.list(query),
    queryFn: ({ signal }) => api.listCalculations(query, signal),
    placeholderData: (previous) => previous,
    enabled: options.enabled ?? true,
  });
}

export function useCalculationDetail(calculationId: string, options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.calculations.detail(calculationId),
    queryFn: ({ signal }) => api.getCalculation(calculationId, signal),
    enabled: Boolean(calculationId) && (options.enabled ?? true),
  });
}

/** Imported lot upload receipt (rows, parser errors, artifact metadata). */
export function useLotUpload(uploadId: string | null | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.calculations.upload(uploadId ?? ""),
    queryFn: () => api.getLotUpload(uploadId ?? ""),
    enabled: Boolean(uploadId),
    staleTime: 5 * 60_000,
  });
}

/**
 * Job audit list. It refetches on its own while any listed job is queued or
 * running, and stops as soon as every job on the page has settled.
 */
export function useCalculationJobs(
  query: CalculationJobQuery,
  options: { enabled?: boolean } = {},
) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.calculationJobs.list(query),
    queryFn: ({ signal }) => api.listCalculationJobs(query, signal),
    placeholderData: (previous) => previous,
    enabled: options.enabled ?? true,
    refetchInterval: (queryState) =>
      calculationJobsPollInterval({
        items: queryState.state.data?.items,
        error: queryState.state.error,
      }),
  });
}

/* -- Custo estimado da infraestrutura ------------------------------------ */

export function useInfraCostEstimate(options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.infraCost,
    queryFn: ({ signal }) => api.getInfraCostEstimate(signal),
    // The model changes with a deploy and usage moves slowly: no polling.
    staleTime: 5 * 60_000,
    enabled: options.enabled ?? true,
  });
}

/* -- Planilhas com erro ------------------------------------------------- */

export function useUploadFailures(query: UploadFailureQuery, options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.uploadFailures.list(query),
    queryFn: ({ signal }) => api.listUploadFailures(query, signal),
    placeholderData: (previous) => previous,
    enabled: options.enabled ?? true,
  });
}

export function useUploadFailure(failureId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.uploadFailures.detail(failureId ?? ""),
    queryFn: ({ signal }) => api.getUploadFailure(failureId ?? "", signal),
    enabled: Boolean(failureId),
    retry: (count, error) =>
      !(error instanceof TaxApiError && (error.status === 404 || error.status === 403)) &&
      count < 2,
  });
}

function retryUnlessTerminal(count: number, error: unknown): boolean {
  if (
    error instanceof TaxApiError &&
    (error.status === 403 ||
      error.status === 404 ||
      error.code === "PERMISSION_DENIED" ||
      error.code === "NOT_FOUND")
  ) {
    return false;
  }
  return count < 2;
}

/** Marks a failure reviewed (or reopens it) and saves the note; refreshes list and detail. */
export function useReviewUploadFailure() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { failureId: string; review: UploadFailureReviewRequest }) =>
      api.reviewUploadFailure(input.failureId, input.review),
    onSuccess: (detail: UploadFailureDetail) => {
      queryClient.setQueryData(queryKeys.uploadFailures.detail(detail.id), detail);
      void queryClient.invalidateQueries({ queryKey: ["upload-failures", "list"] });
    },
  });
}

/* -- Sessões ---------------------------------------------------------------- */

const SESSION_EVENTS_CHUNK_CAP = 400;
const SESSION_ACTIVITY_CAP = 2_000;

export type SessionReplayEventsResult = {
  events: Array<Record<string, unknown>>;
  /** True when reading stopped at 400 chunks. */
  truncated: boolean;
  loadedChunks: number;
};

export type SessionReplayActivityResult = {
  items: Awaited<ReturnType<TaxApiClient["listSessionReplayActivity"]>>["items"];
  truncated: boolean;
};

export function useSessionReplays(query: SessionReplayQuery, options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.sessionReplays.list(query),
    queryFn: ({ signal }) => api.listSessionReplays(query, signal),
    placeholderData: (previous) => previous,
    enabled: options.enabled ?? true,
    retry: retryUnlessTerminal,
  });
}

export function useSessionReplay(replaySessionId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.sessionReplays.detail(replaySessionId ?? ""),
    queryFn: ({ signal }) => api.getSessionReplay(replaySessionId ?? "", signal),
    enabled: Boolean(replaySessionId),
    retry: retryUnlessTerminal,
  });
}

/**
 * Pages event chunks until `nextSequence` is null. Stops after 400 chunks.
 * `onProgress` is stored in a ref so a new callback does not refetch.
 */
export function useSessionReplayEvents(
  replaySessionId: string | undefined,
  options: { enabled?: boolean; onProgress?: (loadedChunks: number) => void } = {},
) {
  const api = useApi();
  const progressRef = useRef(options.onProgress);
  progressRef.current = options.onProgress;
  return useQuery({
    queryKey: queryKeys.sessionReplays.events(replaySessionId ?? ""),
    enabled: Boolean(replaySessionId) && (options.enabled ?? true),
    retry: retryUnlessTerminal,
    queryFn: async ({ signal }): Promise<SessionReplayEventsResult> => {
      const events: Array<Record<string, unknown>> = [];
      let fromSequence = 0;
      let loadedChunks = 0;
      let truncated = false;
      const seen = new Set<number>();
      while (loadedChunks < SESSION_EVENTS_CHUNK_CAP) {
        const page = await api.listSessionReplayEvents(
          replaySessionId ?? "",
          { fromSequence, maxChunks: 10 },
          signal,
        );
        events.push(...page.events);
        if (page.nextSequence == null) {
          loadedChunks = Math.max(loadedChunks, fromSequence + (page.events.length > 0 ? 1 : 0));
          break;
        }
        if (seen.has(page.nextSequence) || page.nextSequence <= fromSequence) break;
        seen.add(page.nextSequence);
        loadedChunks = page.nextSequence;
        fromSequence = page.nextSequence;
        progressRef.current?.(loadedChunks);
        if (loadedChunks >= SESSION_EVENTS_CHUNK_CAP) {
          truncated = true;
          break;
        }
      }
      progressRef.current?.(loadedChunks);
      return { events, truncated, loadedChunks };
    },
  });
}

/** Every activity row of one session, oldest first, capped at 2,000. */
export function useSessionReplayActivity(
  replaySessionId: string | undefined,
  options: { enabled?: boolean } = {},
) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.sessionReplays.activity(replaySessionId ?? ""),
    enabled: Boolean(replaySessionId) && (options.enabled ?? true),
    retry: retryUnlessTerminal,
    queryFn: async ({ signal }): Promise<SessionReplayActivityResult> => {
      const items: SessionReplayActivityResult["items"] = [];
      let cursor: string | undefined;
      let truncated = false;
      const seen = new Set<string>();
      while (items.length < SESSION_ACTIVITY_CAP) {
        const page = await api.listSessionReplayActivity(
          replaySessionId ?? "",
          { cursor, limit: 500 },
          signal,
        );
        items.push(...page.items);
        if (!page.hasMore || !page.nextCursor) break;
        if (seen.has(page.nextCursor)) break;
        seen.add(page.nextCursor);
        if (items.length >= SESSION_ACTIVITY_CAP) {
          truncated = true;
          break;
        }
        cursor = page.nextCursor;
      }
      if (items.length > SESSION_ACTIVITY_CAP) {
        items.length = SESSION_ACTIVITY_CAP;
        truncated = true;
      }
      return { items, truncated };
    },
  });
}

export function useSessionActivity(
  query: SessionActivityQuery,
  options: { enabled?: boolean } = {},
) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.sessionActivity.list(query),
    queryFn: ({ signal }) => api.listSessionActivity(query, signal),
    placeholderData: (previous) => previous,
    enabled: options.enabled ?? true,
    retry: retryUnlessTerminal,
  });
}

/** Resolves a request, upload failure or subject to a session. Quiet on a miss. */
export function useSessionReplayLookup(query: SessionReplayLookupQuery | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: query
      ? queryKeys.sessionReplayLookup(query)
      : (["session-replay-lookup", "idle"] as const),
    queryFn: ({ signal }) => api.lookupSessionReplay(query as SessionReplayLookupQuery, signal),
    enabled: query !== undefined,
    retry: false,
  });
}

export function useSessionReplaySettings(options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.sessionReplaySettings,
    queryFn: ({ signal }) => api.getSessionReplaySettings(signal),
    enabled: options.enabled ?? true,
    retry: retryUnlessTerminal,
  });
}

export function useUpdateSessionReplaySettings() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SessionReplaySettingsUpdate) => api.updateSessionReplaySettings(input),
    onSuccess: (settings) => {
      queryClient.setQueryData(queryKeys.sessionReplaySettings, settings);
    },
  });
}

/** Fetches the stored original and hands it to the browser under its own file name. */
export function useDownloadUploadFailureFile() {
  const api = useApi();
  return useMutation({
    mutationFn: async (input: { failureId: string; fileName: string; contentType?: string }) => {
      const bytes = await api.downloadUploadFailureFile(input.failureId);
      const url = URL.createObjectURL(
        new Blob([bytes], { type: input.contentType ?? "application/octet-stream" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = input.fileName;
      link.hidden = true;
      document.body.append(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      return bytes.byteLength;
    },
  });
}

export function useCalculationMemory(calculationId: string, itemId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ["calculation-memory", calculationId, itemId ?? ""],
    queryFn: ({ signal }) =>
      api.getCalculationMemory({ calculationId, itemId: itemId ?? "" }, signal),
    enabled: Boolean(itemId),
    retry: false,
  });
}

export function useCalculationExplain(calculationId: string, itemId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.calculations.explain(calculationId, itemId ?? ""),
    queryFn: ({ signal }) =>
      api.explainCalculationItem({ calculationId, itemId: itemId ?? "" }, signal),
    enabled: Boolean(itemId),
  });
}

export function useFiscalTaskList(query: FiscalTaskQuery) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.fiscalTasks.list(query),
    queryFn: ({ signal }) => api.listFiscalTasks(query, signal),
    placeholderData: (previous) => previous,
    refetchInterval: (queryState) => {
      if (queryState.state.error) return false;
      return queryState.state.data?.items.some((task) => isFiscalTaskInFlight(task.status))
        ? FISCAL_TASK_POLL_INTERVAL_MS
        : false;
    },
  });
}

/**
 * How many lots the runner takes before a queued one: every running lot and every queued lot
 * created earlier (the runner claims queued work oldest first). Read from the same list the
 * Lotes page uses; `null` while unknown, so the screen never shows an invented position.
 */
export function useFiscalTaskQueueAhead(
  task: Pick<FiscalTask, "id" | "status" | "createdAt"> | undefined,
) {
  const api = useApi();
  const queued = task?.status === "QUEUED";
  const query = useQuery({
    queryKey: ["fiscal-tasks", "queue-ahead", task?.id],
    queryFn: ({ signal }) =>
      api.listFiscalTasks({ status: ["QUEUED", "RUNNING"], limit: 100 }, signal),
    enabled: queued,
    refetchInterval: queued ? FISCAL_TASK_POLL_MAX_MS : false,
    refetchIntervalInBackground: true,
    retry: false,
  });
  if (!queued || !task || !query.data) return null;
  const createdAt = Date.parse(task.createdAt);
  return query.data.items.filter(
    (other) =>
      other.id !== task.id &&
      (other.status === "RUNNING" ||
        (other.status === "QUEUED" && Date.parse(other.createdAt) < createdAt)),
  ).length;
}

export function useFiscalTask(taskId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: queryKeys.fiscalTasks.detail(taskId),
    queryFn: async ({ signal }) => {
      const task = await api.getFiscalTask(taskId, signal);
      if (!isFiscalTaskInFlight(task.status)) {
        void queryClient.invalidateQueries({
          queryKey: ["fiscal-tasks", "results", taskId],
        });
      }
      return task;
    },
    enabled: Boolean(taskId),
    ...FISCAL_TASK_LIVE_OPTIONS,
    refetchInterval: (query) =>
      fiscalTaskPollInterval({
        status: query.state.data?.status,
        error: query.state.error,
        polls: query.state.dataUpdateCount,
      }),
  });
}

/**
 * Every result row of a finished lot, read page by page, for lot-wide sums the analysis does not
 * carry (sale price, PIS/COFINS on the sale, margin). Keyed by revision so a recalculation reloads.
 */
export function useFiscalTaskAllResults(
  task: Pick<FiscalTask, "id" | "revision" | "status"> | undefined,
  options: { enabled?: boolean } = {},
) {
  const api = useApi();
  const finished = Boolean(task) && !isFiscalTaskInFlight(task?.status ?? "QUEUED");
  return useQuery({
    queryKey: ["fiscal-tasks", "results", task?.id, "all", task?.revision],
    queryFn: async ({ signal }) => {
      const rows = [];
      let cursor: string | null = null;
      do {
        const page = await api.listFiscalTaskResults(
          task?.id ?? "",
          { cursor, limit: 200, sort: "position", dir: "asc" },
          signal,
        );
        rows.push(...page.items);
        cursor = page.hasMore ? page.nextCursor : null;
      } while (cursor);
      return rows;
    },
    enabled: finished && (options.enabled ?? true),
    staleTime: 60_000,
  });
}

export function useFiscalTaskResults(
  taskId: string,
  query: FiscalTaskResultQuery = {},
  options: { enabled?: boolean } = {},
) {
  const api = useApi();
  const queryClient = useQueryClient();
  const pageQuery = {
    cursor: query.cursor,
    limit: query.limit ?? DEFAULT_PAGE_SIZE,
    itemId: query.itemId,
    q: query.q,
    status: query.status,
    route: query.route,
    ncm: query.ncm,
    issue: query.issue,
    sort: query.sort ?? "position",
    dir: query.dir ?? "asc",
  } satisfies FiscalTaskResultQuery;
  return useQuery({
    queryKey: queryKeys.fiscalTasks.results(taskId, pageQuery),
    queryFn: ({ signal }) => api.listFiscalTaskResults(taskId, pageQuery, signal),
    enabled: Boolean(taskId) && (options.enabled ?? true),
    placeholderData: (previous) => previous,
    ...FISCAL_TASK_LIVE_OPTIONS,
    refetchInterval: (query) => {
      const task = queryClient.getQueryState<{ status?: string }>(
        queryKeys.fiscalTasks.detail(taskId),
      );
      return fiscalTaskPollInterval({
        status: task?.data?.status,
        error: query.state.error ?? task?.error,
        polls: query.state.dataUpdateCount,
      });
    },
  });
}

export function useFiscalTaskMutations() {
  const api = useApi();
  const queryClient = useQueryClient();
  const invalidate = (taskId: string) => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.fiscalTasks.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.fiscalTasks.detail(taskId) });
    void queryClient.invalidateQueries({ queryKey: ["fiscal-tasks", "results", taskId] });
    void queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
  };
  return {
    cancel: useMutation({
      mutationFn: (input: { taskId: string; revision?: number; idempotencyKey?: string }) =>
        api.cancelFiscalTask(input),
      onSuccess: (task) => {
        queryClient.setQueryData(queryKeys.fiscalTasks.detail(task.id), task);
        invalidate(task.id);
      },
    }),
    retry: useMutation({
      mutationFn: (input: {
        taskId: string;
        reason?: string;
        revision?: number;
        idempotencyKey?: string;
      }) => api.retryFiscalTask(input),
      onSuccess: (task) => {
        queryClient.setQueryData(queryKeys.fiscalTasks.detail(task.id), task);
        invalidate(task.id);
      },
    }),
  };
}

export const FISCAL_RESEARCH_POLL_INTERVAL_MS = 2_000;

export function isFiscalResearchTaskInFlight(
  status: string | undefined,
  citations?: ReadonlyArray<{ status: string }>,
): boolean {
  return (
    status === "QUEUED" ||
    status === "RUNNING" ||
    citations?.some(
      (citation) => citation.status === "QUEUED" || citation.status === "FETCHING",
    ) === true
  );
}

export function useCoverageGapList(
  query: CoverageGapQuery = {},
  options: { enabled?: boolean } = {},
) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.coverageGaps.list(query),
    queryFn: ({ signal }) => api.listCoverageGaps(query, signal),
    enabled: options.enabled ?? true,
    placeholderData: (previous) => previous,
    // Research runs asynchronously; keep an eye on gaps while any is in flight.
    refetchInterval: (queryState) =>
      queryState.state.data?.items.some((gap) => gap.status === "RESEARCHING")
        ? FISCAL_RESEARCH_POLL_INTERVAL_MS
        : false,
  });
}

export function useCoverageGapMutations() {
  const api = useApi();
  const queryClient = useQueryClient();
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.coverageGaps.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.fiscalResearchTasks.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
  };
  return {
    research: useMutation({
      mutationFn: (input: Parameters<typeof api.researchCoverageGap>[0]) =>
        api.researchCoverageGap(input),
      onSuccess: refresh,
    }),
    researchNext: useMutation({
      mutationFn: (input: Parameters<typeof api.researchNextCoverageGaps>[0]) =>
        api.researchNextCoverageGaps(input),
      onSuccess: refresh,
    }),
    finding: useMutation({
      mutationFn: (input: Parameters<typeof api.recordCoverageGapFinding>[0]) =>
        api.recordCoverageGapFinding(input),
      onSuccess: refresh,
    }),
    dismiss: useMutation({
      mutationFn: (input: Parameters<typeof api.dismissCoverageGap>[0]) =>
        api.dismissCoverageGap(input),
      onSuccess: refresh,
    }),
  };
}

export function useFiscalResearchTaskList(
  query: FiscalResearchTaskQuery = {},
  options: { enabled?: boolean } = {},
) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.fiscalResearchTasks.list(query),
    queryFn: ({ signal }) => api.listFiscalResearchTasks(query, signal),
    enabled: options.enabled ?? true,
    placeholderData: (previous) => previous,
    refetchInterval: (queryState) => {
      if (queryState.state.error) return false;
      return queryState.state.data?.items.some((task) =>
        isFiscalResearchTaskInFlight(task.status, task.citations),
      )
        ? FISCAL_RESEARCH_POLL_INTERVAL_MS
        : false;
    },
  });
}

export function useFiscalResearchTask(taskId: string) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.fiscalResearchTasks.detail(taskId),
    queryFn: ({ signal }) => api.getFiscalResearchTask(taskId, signal),
    enabled: Boolean(taskId),
    staleTime: 0,
    refetchInterval: (queryState) => {
      if (queryState.state.error) return false;
      return isFiscalResearchTaskInFlight(
        queryState.state.data?.status,
        queryState.state.data?.citations,
      )
        ? FISCAL_RESEARCH_POLL_INTERVAL_MS
        : false;
    },
  });
}

export function useFiscalResearchMutations() {
  const api = useApi();
  const queryClient = useQueryClient();
  return {
    retry: useMutation({
      mutationFn: (input: Parameters<typeof api.retryFiscalResearchDispatch>[0]) =>
        api.retryFiscalResearchDispatch(input),
      onSuccess: (task) => {
        queryClient.setQueryData(queryKeys.fiscalResearchTasks.detail(task.id), task);
        void queryClient.invalidateQueries({ queryKey: queryKeys.fiscalResearchTasks.all });
        void queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      },
    }),
    create: useMutation({
      mutationFn: (input: { task: FiscalResearchTaskCreate; idempotencyKey?: string }) =>
        api.createFiscalResearchTask(input.task, input.idempotencyKey),
      onSuccess: (task) => {
        queryClient.setQueryData(queryKeys.fiscalResearchTasks.detail(task.id), task);
        void queryClient.invalidateQueries({ queryKey: queryKeys.fiscalResearchTasks.all });
        void queryClient.invalidateQueries({ queryKey: queryKeys.audit.all });
      },
    }),
  };
}

export function useSourceList() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.sources.list,
    queryFn: ({ signal }) => api.listSources(signal),
    refetchInterval: 60_000,
  });
}

export function useSourceDetail(sourceId: string) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.sources.detail(sourceId),
    queryFn: ({ signal }) => api.getSource(sourceId, signal),
  });
}

export function useSourceRun(runId: string, options: { enabled?: boolean } = {}) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: queryKeys.sourceRuns.detail(runId),
    queryFn: async ({ signal }) => {
      const run = await api.getSourceRun(runId, signal);
      if (!isSourceRunInFlight(run.status)) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.sourceRuns.evidenceRoot(runId) });
        void queryClient.invalidateQueries({ queryKey: queryKeys.evidence.all });
        // Returning from the run must show its terminal state in source history.
        void queryClient.invalidateQueries({ queryKey: queryKeys.sources.all });
      }
      return run;
    },
    enabled: Boolean(runId) && (options.enabled ?? true),
    staleTime: 0,
    refetchInterval: (query) =>
      sourceRunPollInterval({ status: query.state.data?.status, error: query.state.error }),
  });
}

export function useSourceRunEvidence(
  runId: string,
  query: SourceRunEvidenceQuery = {},
  options: { enabled?: boolean } = {},
) {
  const api = useApi();
  const queryClient = useQueryClient();
  const pageQuery = {
    cursor: query.cursor,
    limit: query.limit ?? DEFAULT_PAGE_SIZE,
    sort: query.sort ?? "observedAt",
    dir: query.dir ?? "desc",
  } satisfies SourceRunEvidenceQuery;

  return useQuery({
    queryKey: queryKeys.sourceRuns.evidence(runId, pageQuery),
    queryFn: ({ signal }) => api.listSourceRunEvidence(runId, pageQuery, signal),
    enabled: Boolean(runId) && (options.enabled ?? true),
    staleTime: 0,
    refetchInterval: (queryState) => {
      if (queryState.state.error) return false;
      const run = queryClient.getQueryState<SourceRun>(queryKeys.sourceRuns.detail(runId));
      return sourceRunPollInterval({ status: run?.data?.status, error: run?.error });
    },
  });
}

export function useCancelSourceRun() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.cancelSourceRun,
    onSuccess: async (_result, input) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.sourceRuns.detail(input.runId) }),
        queryClient.invalidateQueries({ queryKey: queryKeys.sources.all }),
        queryClient.invalidateQueries({ queryKey: queryKeys.overview }),
        queryClient.invalidateQueries({ queryKey: queryKeys.audit.all }),
      ]);
    },
  });
}

export function useSourceMutations() {
  const api = useApi();
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.sources.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.overview });
    void queryClient.invalidateQueries({ queryKey: queryKeys.evidence.all });
  };

  return {
    runNow: useMutation({
      mutationFn: (input: {
        sourceId: string;
        reason?: string;
        maxDocuments?: number;
        idempotencyKey?: string;
      }) => api.triggerSourceRun(input),
      onSuccess: invalidate,
    }),
    linkRun: useMutation({
      mutationFn: (input: LinkRunRequest & { idempotencyKey?: string }) => api.createLinkRun(input),
      onSuccess: invalidate,
    }),
    setPaused: useMutation({ mutationFn: api.setSourcePaused, onSuccess: invalidate }),
    replayDlq: useMutation({
      mutationFn: (input: { sourceId: string; request: DlqReplayRequest }) =>
        api.replaySourceDlq(input),
      onSuccess: invalidate,
    }),
  };
}

export function useAuditList(query: AuditQuery, options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.audit.list(query),
    queryFn: ({ signal }) => api.listAudit(query, signal),
    placeholderData: (previous) => previous,
    enabled: options.enabled ?? true,
  });
}

export function useAuditDetail(auditId: string) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.audit.detail(auditId),
    queryFn: async ({ signal }) => {
      const page = await api.listAudit({ q: auditId, limit: 50 }, signal);
      const match = exactRecord(page.items, auditId);
      if (!match) throw recordNotFound("audit event");
      return match;
    },
    enabled: Boolean(auditId),
  });
}

/* -- Browser agent spectator (ADR 0012) ----------------------------------- */

export function useBrowserAgentRuns(query: BrowserAgentRunQuery = { limit: 20 }) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.browserAgent.runs(query),
    queryFn: ({ signal }) => api.listBrowserAgentRuns(query, signal),
    placeholderData: (previous) => previous,
    refetchInterval: (state) =>
      state.state.error
        ? false
        : state.state.data?.items.some((run) => run.status === "RUNNING")
          ? BROWSER_AGENT_RUN_POLL_MS * 2
          : 30_000,
  });
}

/** The agent's last work poll, refreshed every minute. */
export function useBrowserAgentPresence() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.browserAgent.presence,
    queryFn: ({ signal }) => api.getBrowserAgentPresence(signal),
    refetchInterval: (state) => (state.state.error ? false : 60_000),
  });
}

export function useBrowserAgentRun(runId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.browserAgent.run(runId ?? ""),
    queryFn: ({ signal }) => api.getBrowserAgentRun(runId ?? "", signal),
    enabled: Boolean(runId),
    refetchInterval: (state) =>
      !state.state.error && state.state.data?.status === "RUNNING"
        ? BROWSER_AGENT_RUN_POLL_MS
        : false,
  });
}

/**
 * The step log is incremental: each poll asks only for `after=<lastSequence>`
 * and merges into the cached log, so a long run never re-downloads history.
 */
export function useBrowserAgentSteps(runId: string | undefined, running: boolean) {
  const api = useApi();
  const queryClient = useQueryClient();
  const key = queryKeys.browserAgent.steps(runId ?? "");
  return useQuery({
    queryKey: key,
    queryFn: async ({ signal }): Promise<StepLog> => {
      const previous = queryClient.getQueryData<StepLog>(key);
      const page = await api.listBrowserAgentSteps(runId ?? "", previous?.lastSequence, signal);
      return mergeSteps(previous, page);
    },
    enabled: Boolean(runId),
    refetchInterval: (state) =>
      running && !state.state.error ? BROWSER_AGENT_STEP_POLL_MS : false,
  });
}

export function useBrowserAgentFindings(query: BrowserAgentFindingQuery, running: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.browserAgent.findings(query),
    queryFn: ({ signal }) => api.listBrowserAgentFindings(query, signal),
    enabled: Boolean(query.runId),
    placeholderData: (previous) => previous,
    refetchInterval: (state) => (running && !state.state.error ? 10_000 : false),
  });
}

/** Frames are immutable per generation: fetch once, keep as a Blob. */
export function useBrowserAgentFrame(
  frame: { bucket: string; key: string; generation: string } | null | undefined,
  options: { enabled?: boolean } = {},
) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.browserAgent.frame(frame?.key ?? "", frame?.generation ?? ""),
    queryFn: async ({ signal }) => {
      if (!frame) throw new Error("No frame");
      const bytes = await api.getBrowserAgentFrame(frame, signal);
      const view = new Uint8Array(bytes);
      return new Blob([view], { type: sniffImageType(view) });
    },
    enabled: Boolean(frame) && (options.enabled ?? true),
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: 5 * 60_000,
    retry: 1,
  });
}

/** Single-use, 60-second ticket for the read-only VNC viewer. */
export function useBrowserAgentViewerTicket() {
  const api = useApi();
  return useMutation({ mutationFn: () => api.createBrowserAgentViewerTicket() });
}

export function useStopBrowserAgentRun() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (runId: string) => api.stopBrowserAgentRun(runId),
    onSuccess: (run) => {
      queryClient.setQueryData(queryKeys.browserAgent.run(run.id), run);
      // Frames are immutable; refresh everything else about the agent.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.browserAgent.all,
        predicate: (query) => query.queryKey[1] !== "frame",
      });
    },
  });
}

/* -- Registros do agente (findings audit) ------------------------------------ */

/** Upper bound of findings pulled into the browser for one export. */
export const FINDINGS_EXPORT_MAX = 20_000;

export function useBrowserAgentFindingDaily(from: string, to: string) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.browserAgent.findingDaily(from, to),
    queryFn: ({ signal }) => api.getBrowserAgentFindingDaily(from, to, signal),
    placeholderData: (previous) => previous,
  });
}

/** Every finding of one Brasília day (the agent's hourly cap bounds a day to ~1,440). */
export function useBrowserAgentDayFindings(day: string) {
  const collect = useCollectBrowserAgentFindings();
  return useQuery({
    queryKey: queryKeys.browserAgent.dayFindings(day),
    queryFn: ({ signal }) => collect({ from: day, to: day }, signal),
    placeholderData: (previous) => previous,
  });
}

/** Pages through `/v1/browser-agent/findings` for a range (server-side filters applied). */
export function useCollectBrowserAgentFindings() {
  const api = useApi();
  return useCallback(
    async (
      query: Omit<BrowserAgentFindingQuery, "cursor" | "limit">,
      signal?: AbortSignal,
    ): Promise<{ items: BrowserAgentFinding[]; truncated: boolean }> => {
      const items: BrowserAgentFinding[] = [];
      let cursor: string | null = null;
      do {
        const page = await api.listBrowserAgentFindings({ ...query, cursor, limit: 200 }, signal);
        items.push(...page.items);
        cursor = page.hasMore ? page.nextCursor : null;
      } while (cursor && items.length < FINDINGS_EXPORT_MAX);
      return { items, truncated: Boolean(cursor) };
    },
    [api],
  );
}

/* -- Browser agent recordings and demos ------------------------------------- */

/** A run's recording segments; polled while the run is live so "Salvar como demo" enables. */
export function useBrowserAgentRunRecordings(runId: string | undefined, running = false) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.browserAgent.recordings(runId ?? ""),
    queryFn: ({ signal }) => api.listBrowserAgentRunRecordings(runId ?? "", signal),
    enabled: Boolean(runId),
    refetchInterval: (state) => (running && !state.state.error ? 30_000 : false),
  });
}

/** Signed URLs live 10 minutes: refetch well before they expire. */
export const RECORDING_URL_REFRESH_MS = 8 * 60_000;

export function useBrowserAgentRecordingUrl(
  recordingId: string | undefined,
  demoId: string | undefined,
) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.browserAgent.recordingUrl(recordingId ?? "", demoId),
    queryFn: ({ signal }) => api.getBrowserAgentRecordingUrl(recordingId ?? "", demoId, signal),
    enabled: Boolean(recordingId),
    staleTime: RECORDING_URL_REFRESH_MS,
    refetchInterval: RECORDING_URL_REFRESH_MS,
    refetchOnWindowFocus: false,
    retry: 1,
  });
}

export function useBrowserAgentDemos() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.browserAgent.demos,
    queryFn: ({ signal }) => api.listBrowserAgentDemos({ limit: 100 }, signal),
  });
}

export function useBrowserAgentDemo(demoId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.browserAgent.demo(demoId ?? ""),
    queryFn: ({ signal }) => api.getBrowserAgentDemo(demoId ?? "", signal),
    enabled: Boolean(demoId),
  });
}

export function useCreateBrowserAgentDemo() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BrowserAgentDemoCreate) => api.createBrowserAgentDemo(input),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.browserAgent.demos }),
  });
}

export function useDeleteBrowserAgentDemo() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (demoId: string) => api.deleteBrowserAgentDemo(demoId),
    onSuccess: (_result, demoId) => {
      queryClient.removeQueries({ queryKey: queryKeys.browserAgent.demo(demoId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.browserAgent.demos });
    },
  });
}

/* -- Browser agent phase 2: settings, credentials, task queue, lookup jobs -- */

const QUEUE_POLL_MS = 15_000;

export function useBrowserAgentSettings() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.browserAgent.settings,
    queryFn: ({ signal }) => api.getBrowserAgentSettings(signal),
    refetchInterval: (state) => (state.state.error ? false : 60_000),
  });
}

function refreshQueue(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.browserAgent.tasks });
  void queryClient.invalidateQueries({ queryKey: ["browser-agent", "task"] });
  void queryClient.invalidateQueries({ queryKey: ["browser-agent", "task-items"] });
}

export function useUpdateBrowserAgentSettings() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { update: BrowserAgentSettingsUpdate; revision: number }) =>
      api.updateBrowserAgentSettings(input.update, input.revision),
    onSuccess: (settings: BrowserAgentSettings) => {
      queryClient.setQueryData(queryKeys.browserAgent.settings, settings);
      // A pause or resume changes which task runs.
      refreshQueue(queryClient);
    },
  });
}

export function useBrowserAgentCredentials() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.browserAgent.credentials,
    queryFn: ({ signal }) => api.getBrowserAgentCredentials(signal),
    // 503 means Secret Manager is not configured: a stable state, not a blip.
    retry: false,
  });
}

/**
 * Saving IOB credentials deliberately bypasses `useMutation`: React Query
 * keeps mutation variables in its cache (and devtools), and the password must
 * never live anywhere but the form field and the single request. Only the
 * masked status the API returns is cached.
 */
export function useSaveBrowserAgentCredentials() {
  const api = useApi();
  const queryClient = useQueryClient();
  const [state, setState] = useState<{ pending: boolean; error: unknown }>({
    pending: false,
    error: null,
  });
  const save = useCallback(
    async (
      update: BrowserAgentCredentialsUpdate,
    ): Promise<BrowserAgentCredentialsStatus | null> => {
      setState({ pending: true, error: null });
      try {
        const status = await api.updateBrowserAgentCredentials(update);
        queryClient.setQueryData(queryKeys.browserAgent.credentials, status);
        setState({ pending: false, error: null });
        return status;
      } catch (error) {
        setState({ pending: false, error });
        return null;
      }
    },
    [api, queryClient],
  );
  return { save, isPending: state.pending, error: state.error };
}

export function useBrowserAgentTasks() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.browserAgent.tasks,
    queryFn: ({ signal }) => api.listBrowserAgentTasks({ limit: 200 }, signal),
    placeholderData: (previous) => previous,
    refetchInterval: (state) => (state.state.error ? false : QUEUE_POLL_MS),
  });
}

export function useBrowserAgentTask(taskId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.browserAgent.task(taskId ?? ""),
    queryFn: ({ signal }) => api.getBrowserAgentTask(taskId ?? "", signal),
    enabled: Boolean(taskId),
    refetchInterval: (state) =>
      !state.state.error && state.state.data?.status === "RUNNING" ? QUEUE_POLL_MS : false,
  });
}

export function useBrowserAgentTaskItems(
  taskId: string | undefined,
  query: BrowserAgentTaskItemQuery,
  live: boolean,
) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.browserAgent.taskItems(taskId ?? "", query),
    queryFn: ({ signal }) => api.listBrowserAgentTaskItems(taskId ?? "", query, signal),
    enabled: Boolean(taskId),
    placeholderData: (previous) => previous,
    refetchInterval: (state) => (live && !state.state.error ? QUEUE_POLL_MS : false),
  });
}

export function useCreateBrowserAgentTask() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: BrowserAgentTaskCreate) => api.createBrowserAgentTask(input),
    onSuccess: () => refreshQueue(queryClient),
  });
}

export function useBrowserAgentTaskAction() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { taskId: string; action: BrowserAgentTaskAction["action"] }) =>
      api.actOnBrowserAgentTask(input.taskId, input.action),
    onSuccess: (task: BrowserAgentTask) => {
      queryClient.setQueryData(queryKeys.browserAgent.task(task.id), task);
      refreshQueue(queryClient);
    },
  });
}

export function useLookupJobs() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.browserAgent.lookupJobs,
    queryFn: async ({ signal }) => {
      // Console poll used to advance the web lane as a GET side effect. Keep
      // that trigger here so deferred lookups still dispatch; swallow errors
      // (404 while the gateway is not yet updated, 403, network) so the list
      // still loads.
      try {
        await api.advanceLookupJobs(undefined, signal);
      } catch {
        // Best-effort: listing must not depend on the advance succeeding.
      }
      return api.listLookupJobs({ limit: 50 }, signal);
    },
    placeholderData: (previous) => previous,
    refetchInterval: (state) =>
      !state.state.error && state.state.data?.items.some((job) => job.status === "RUNNING")
        ? 30_000
        : false,
  });
}

/* -- Web research sweeps (Administração → Pesquisa web) -------------------- */

const SWEEP_POLL_MS = 15_000;

type ResearchUnitPage = Awaited<ReturnType<TaxApiClient["listResearchUnits"]>>;

function isLive(status: ResearchSweepStatus | undefined): boolean {
  return status !== undefined && isLiveSweep(status);
}

type ResearchSweepPage = Awaited<ReturnType<TaxApiClient["listResearchSweeps"]>>;

/** The newer of two reads of one sweep (runner progress moves `updatedAt`). */
function newerSweep(left: ResearchSweep, right: ResearchSweep): ResearchSweep {
  return Date.parse(right.updatedAt) > Date.parse(left.updatedAt) ? right : left;
}

/** Writes a detail read into the list page so the card never lags the detail. */
function syncSweepIntoList(
  queryClient: ReturnType<typeof useQueryClient>,
  sweep: ResearchSweep,
): void {
  queryClient.setQueryData<ResearchSweepPage>(queryKeys.webResearch.sweeps, (page) => {
    if (!page?.items.some((item) => item.id === sweep.id)) return page;
    return {
      ...page,
      items: page.items.map((item) => (item.id === sweep.id ? newerSweep(item, sweep) : item)),
    };
  });
}

/**
 * Polls while any listed sweep is live. A failed poll does not stop polling
 * for good (the card would freeze on Queued/Running); it slows down instead.
 */
export function useResearchSweeps() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: queryKeys.webResearch.sweeps,
    queryFn: async ({ signal }) => {
      const page = await api.listResearchSweeps({ limit: 50 }, signal);
      // A detail read that is newer than this page wins in both caches.
      const items = page.items.map((item) => {
        const detail = queryClient.getQueryData<ResearchSweep>(
          queryKeys.webResearch.sweep(item.id),
        );
        if (!detail) return item;
        const newer = newerSweep(item, detail);
        if (newer === item && detail.updatedAt !== item.updatedAt) {
          queryClient.setQueryData(queryKeys.webResearch.sweep(item.id), item);
        }
        return newer;
      });
      return { ...page, items };
    },
    placeholderData: (previous) => previous,
    refetchInterval: (state) =>
      state.state.data?.items.some((sweep) => isLive(sweep.status))
        ? state.state.error
          ? SWEEP_POLL_MS * 2
          : SWEEP_POLL_MS
        : false,
  });
}

export function useResearchSweep(sweepId: string | undefined) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: queryKeys.webResearch.sweep(sweepId ?? ""),
    queryFn: async ({ signal }) => {
      const sweep = await api.getResearchSweep(sweepId ?? "", signal);
      syncSweepIntoList(queryClient, sweep);
      return sweep;
    },
    enabled: Boolean(sweepId),
    refetchInterval: (state) =>
      isLive(state.state.data?.status)
        ? state.state.error
          ? SWEEP_POLL_MS * 2
          : SWEEP_POLL_MS
        : false,
  });
}

export type ResearchUnitQuery = {
  kind?: ResearchUnitKind[];
  status?: ResearchUnitStatus[];
  cursor?: string | null;
  limit?: number;
};

export function useResearchUnits(
  sweepId: string | undefined,
  query: ResearchUnitQuery,
  live: boolean,
) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.webResearch.units(sweepId ?? "", query),
    queryFn: ({ signal }) => api.listResearchUnits(sweepId ?? "", query, signal),
    enabled: Boolean(sweepId),
    placeholderData: (previous) => previous,
    refetchInterval: (state) => (live && !state.state.error ? SWEEP_POLL_MS : false),
  });
}

/**
 * Unit counts by stage: one `limit=1` page per (stage, status group); the API
 * answers an exact `totalEstimate`, so no unit list is downloaded.
 */
export function useResearchUnitCounts(
  sweepId: string | undefined,
  groups: ReadonlyArray<{ key: string; kind: ResearchUnitKind[]; status: ResearchUnitStatus[] }>,
  live: boolean,
) {
  const api = useApi();
  return useQueries({
    queries: groups.map((group) => {
      const query: ResearchUnitQuery = { kind: group.kind, status: group.status, limit: 1 };
      return {
        queryKey: queryKeys.webResearch.units(sweepId ?? "", query),
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          api.listResearchUnits(sweepId ?? "", query, signal),
        enabled: Boolean(sweepId),
        placeholderData: (previous: ResearchUnitPage | undefined) => previous,
        refetchInterval: live ? SWEEP_POLL_MS : (false as const),
      };
    }),
    combine: (results) => {
      const counts = new Map<string, number>();
      groups.forEach((group, index) => {
        const total = results[index]?.data?.totalEstimate;
        if (total !== undefined) counts.set(group.key, total);
      });
      return {
        counts,
        isPending: results.some((result) => result.isPending),
        isError: results.some((result) => result.isError),
      };
    },
  });
}

function refreshSweeps(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.webResearch.sweeps });
  void queryClient.invalidateQueries({ queryKey: ["web-research", "units"] });
}

export function usePreviewResearchSweep() {
  const api = useApi();
  return useMutation({
    mutationFn: (input: ResearchSweepPreviewRequest) => api.previewResearchSweep(input),
  });
}

export function useCreateResearchSweep() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { body: ResearchSweepCreate; idempotencyKey: string }) =>
      api.createResearchSweep(input.body, input.idempotencyKey),
    onSuccess: (sweep: ResearchSweep) => {
      queryClient.setQueryData(queryKeys.webResearch.sweep(sweep.id), sweep);
      refreshSweeps(queryClient);
    },
  });
}

export function useResearchSweepAction() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      sweepId: string;
      action: ResearchSweepAction["action"];
      revision: number;
    }) => api.actOnResearchSweep(input.sweepId, input.action, input.revision),
    onSuccess: (sweep: ResearchSweep) => {
      queryClient.setQueryData(queryKeys.webResearch.sweep(sweep.id), sweep);
      syncSweepIntoList(queryClient, sweep);
      refreshSweeps(queryClient);
    },
  });
}

export function useWebResearchSettings() {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.webResearch.settings,
    queryFn: ({ signal }) => api.getWebResearchSettings(signal),
    refetchInterval: (state) => (state.state.error ? false : 60_000),
  });
}

export function useUpdateWebResearchSettings() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { update: WebResearchSettingsUpdate; revision: number }) =>
      api.updateWebResearchSettings(input.update, input.revision),
    onSuccess: (settings: WebResearchSettings) => {
      queryClient.setQueryData(queryKeys.webResearch.settings, settings);
    },
  });
}

/* -- Autoaprovação (ADR 0020) --------------------------------------------- */

export function useAutoApprovalSettings(options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.autoApproval.settings,
    queryFn: ({ signal }) => api.getAutoApprovalSettings(signal),
    enabled: options.enabled ?? true,
  });
}

export function useUpdateAutoApprovalSettings() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { update: AutoApprovalSettingsUpdate; revision: number }) =>
      api.updateAutoApprovalSettings(input.update, input.revision),
    onSuccess: (settings: AutoApprovalSettings) => {
      queryClient.setQueryData(queryKeys.autoApproval.settings, settings);
      void queryClient.invalidateQueries({ queryKey: queryKeys.autoApproval.preview });
      void queryClient.invalidateQueries({ queryKey: queryKeys.autoApproval.runs });
    },
  });
}

/** Read-only preview. Starts idle; call `refetch` from "Pré-visualizar agora". */
export function useAutoApprovalPreview(
  options: { enabled?: boolean; scenario?: "current" | "all" } = {},
) {
  const api = useApi();
  const scenario = options.scenario ?? "current";
  return useQuery({
    queryKey: [...queryKeys.autoApproval.preview, scenario],
    queryFn: ({ signal }) => api.getAutoApprovalPreview(signal, scenario),
    enabled: options.enabled ?? false,
    staleTime: 30_000,
    // A preview that hits the gateway deadline must surface once. The default
    // policy would retry the same 60s request twice more.
    retry: 0,
  });
}

/** Read-only shipped package summary + per-item import status. */
export function useAutoApprovalPackage(options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.autoApproval.package,
    queryFn: ({ signal }) => api.getAutoApprovalPackage(signal),
    enabled: options.enabled ?? true,
    staleTime: 30_000,
  });
}

export function useRunAutoApproval() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (idempotencyKey?: string) => api.runAutoApproval(idempotencyKey),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.autoApproval.runs });
      void queryClient.invalidateQueries({ queryKey: queryKeys.autoApproval.all });
    },
  });
}

export function useAutoApprovalRuns(options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.autoApproval.runs,
    queryFn: ({ signal }) => api.listAutoApprovalRuns(signal),
    enabled: options.enabled ?? true,
  });
}

export function useAutoApprovalReport(date: string, options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.autoApproval.report(date),
    queryFn: ({ signal }) => api.getAutoApprovalReport(date, signal),
    enabled: (options.enabled ?? true) && /^\d{4}-\d{2}-\d{2}$/.test(date),
  });
}

/* -- Fiscal facts (Regras → Verificação, ADR 0013, dormant) ---------------- */

type FiscalFactListQuery = NonNullable<Parameters<TaxApiClient["listFiscalFacts"]>[0]>;

export function useFiscalFactQueue(tab: FiscalFactQueueTab, cursor: string | null, limit = 50) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.fiscalFacts.queue(tab, cursor),
    queryFn: ({ signal }) => api.fiscalFactQueue(tab, { cursor, limit }, signal),
    placeholderData: (previous) => (previous?.tab === tab ? previous : undefined),
  });
}

export type FiscalFactQueueCounts = {
  /** Exact count of the tab; undefined until it is known. */
  get: (tab: FiscalFactQueueTab) => number | undefined;
  /** True when the tab's count could not be read (and none is cached). */
  failed: (tab: FiscalFactQueueTab) => boolean;
};

/** Tab counts: one `limit=1` page per tab; `totalEstimate` is exact. */
export function useFiscalFactQueueCounts(
  tabs: readonly FiscalFactQueueTab[],
): FiscalFactQueueCounts {
  const api = useApi();
  return useQueries({
    queries: tabs.map((tab) => ({
      queryKey: [...queryKeys.fiscalFacts.queue(tab, null), "count"] as const,
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        api.fiscalFactQueue(tab, { limit: 1 }, signal),
    })),
    combine: (results) => {
      const counts = new Map<FiscalFactQueueTab, number>();
      const failed = new Set<FiscalFactQueueTab>();
      tabs.forEach((tab, index) => {
        const result = results[index];
        const total = result?.data?.totalEstimate;
        if (total !== undefined) counts.set(tab, total);
        else if (result?.isError) failed.add(tab);
      });
      return { get: (tab) => counts.get(tab), failed: (tab) => failed.has(tab) };
    },
  });
}

export function useFiscalFacts(query: FiscalFactListQuery, options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.fiscalFacts.list(query),
    queryFn: ({ signal }) => api.listFiscalFacts(query, signal),
    enabled: options.enabled ?? true,
  });
}

export function useFiscalFact(factId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.fiscalFacts.detail(factId ?? ""),
    queryFn: ({ signal }) => api.getFiscalFact(factId ?? "", signal),
    enabled: Boolean(factId),
  });
}

/** Details of several facts (the other side of a conflict). */
export function useFiscalFactDetails(factIds: readonly string[]) {
  const api = useApi();
  return useQueries({
    queries: factIds.map((factId) => ({
      queryKey: queryKeys.fiscalFacts.detail(factId),
      queryFn: ({ signal }: { signal: AbortSignal }) => api.getFiscalFact(factId, signal),
    })),
    combine: (results) => ({
      items: results.flatMap((result) => (result.data ? [result.data] : [])),
      isPending: results.some((result) => result.isPending),
      isError: results.some((result) => result.isError),
    }),
  });
}

export function useFiscalFactReviewGroups(
  query: Partial<FiscalFactReviewGroupQuery> & { cursor?: string | null; limit?: number },
) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.fiscalFacts.reviewGroups(query),
    queryFn: ({ signal }) => api.listFiscalFactReviewGroups(query, signal),
    placeholderData: (previous) =>
      previous?.mode === (query.mode ?? "DOCUMENT") ? previous : undefined,
  });
}

export function useFiscalFactReviewGroup(groupId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.fiscalFacts.reviewGroup(groupId ?? ""),
    queryFn: ({ signal }) => api.getFiscalFactReviewGroup(groupId ?? "", signal),
    enabled: Boolean(groupId),
    placeholderData: (previous) => (previous?.group.id === groupId ? previous : undefined),
  });
}

/** Session writes update the group in place (no refetch between J / Space). */
function patchGroupSession(
  queryClient: ReturnType<typeof useQueryClient>,
  groupId: string,
  session: FiscalFactReviewSession,
) {
  queryClient.setQueryData<FiscalFactReviewGroupDetail>(
    queryKeys.fiscalFacts.reviewGroup(groupId),
    (current) => (current ? { ...current, session } : current),
  );
}

export function useStartFiscalFactReviewSession() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { groupId: string; body: FiscalFactReviewSessionStart }) =>
      api.startFiscalFactReviewSession(input.groupId, input.body),
    onSuccess: (session, input) => {
      patchGroupSession(queryClient, input.groupId, session);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.fiscalFacts.reviewGroup(input.groupId),
      });
    },
  });
}

export function useFiscalFactReviewSessionAction() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      groupId: string;
      sessionId: string;
      body: FiscalFactReviewSessionAction;
    }) => api.actOnFiscalFactReviewSession(input.sessionId, input.body),
    onSuccess: (session, input) => {
      patchGroupSession(queryClient, input.groupId, session);
      // Sample, staleness and group state are server-computed: refresh them quietly.
      if (input.body.action !== "INSPECT" || input.body.outcome === "DISCREPANCY") {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.fiscalFacts.reviewGroup(input.groupId),
        });
      }
    },
  });
}

export function useConfirmFiscalFactReviewSession() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { groupId: string; sessionId: string; body: FiscalFactGroupConfirm }) =>
      api.confirmFiscalFactReviewSession(input.sessionId, input.body),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.fiscalFacts.all });
    },
  });
}

export function useFiscalFactConfirmedReviews(
  cursor: string | null = null,
  options: { enabled?: boolean } = {},
) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.fiscalFacts.confirmedReviews(cursor),
    queryFn: ({ signal }) => api.listFiscalFactConfirmedReviews({ cursor, limit: 50 }, signal),
    enabled: options.enabled ?? true,
  });
}

/** "Corrigir escopo" (session) or "Restringir escopo de origem" (one fact, If-Match). */
export function useRestrictFiscalFactScope() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (
      input:
        | { target: "SESSION"; sessionId: string; body: FiscalFactScopeRestriction }
        | { target: "FACT"; factId: string; revision: number; body: FiscalFactScopeRestriction },
    ) =>
      input.target === "SESSION"
        ? api.restrictFiscalFactReviewSessionScope(input.sessionId, input.body)
        : api.restrictFiscalFactScope(input.factId, input.body, input.revision),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.fiscalFacts.all });
    },
  });
}

export function useVerifyFiscalFact() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { factId: string; body: FiscalFactVerification; revision: number }) =>
      api.verifyFiscalFact(input.factId, input.body, input.revision),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.fiscalFacts.all });
    },
  });
}

export function useStAgreements(
  query: { status?: StAgreement["status"]; segment?: string; uf?: string } = {},
  options: { enabled?: boolean } = {},
) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.fiscalFacts.agreements(query),
    queryFn: ({ signal }) => api.listStAgreements(query, signal),
    enabled: options.enabled ?? true,
  });
}

export function useStAgreement(agreementId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.fiscalFacts.agreement(agreementId ?? "none"),
    queryFn: ({ signal }) => api.getStAgreement(agreementId as string, signal),
    enabled: Boolean(agreementId),
  });
}

/** What a retire run would reject (the API's dry run writes nothing). */
export function useMisidentifiedStAgreements(options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.fiscalFacts.misidentified,
    queryFn: () =>
      api.retireMisidentifiedStAgreements({
        schemaVersion: "1.0.0",
        dryRun: true,
        reason: "Preview of misidentified observations",
      }),
    enabled: options.enabled ?? true,
    staleTime: 0,
  });
}

/** Dry run lists the misidentified observations; a run rejects them (audited). */
export function useRetireMisidentifiedStAgreements() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: StAgreementRetireMisidentified) => api.retireMisidentifiedStAgreements(body),
    onSuccess: (result) => {
      if (!result.dryRun) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.fiscalFacts.all });
      }
    },
  });
}

/** Reviewing an agreement changes every group proposal: refresh all fiscal fact reads. */
export function useVerifyStAgreement() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      agreementId: string;
      body: StAgreementVerification;
      revision: number;
    }) => api.verifyStAgreement(input.agreementId, input.body, input.revision),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.fiscalFacts.all });
    },
  });
}

export function useFiscalFactsSettings(options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.fiscalFacts.settings,
    queryFn: ({ signal }) => api.getFiscalFactsSettings(signal),
    enabled: options.enabled ?? true,
  });
}

export function useFactsShadowSummary(days: number, options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.fiscalFacts.shadowSummary(days),
    queryFn: ({ signal }) => api.getFactsShadowSummary(days, signal),
    enabled: options.enabled ?? true,
  });
}

export function useUpdateFiscalFactsSettings() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { update: FiscalFactsSettingsUpdate; revision: number }) =>
      api.updateFiscalFactsSettings(input.update, input.revision),
    onSuccess: (settings: FiscalFactsSettings) => {
      queryClient.setQueryData(queryKeys.fiscalFacts.settings, settings);
    },
  });
}

/* -- Next draft ruleset and proposal drafts (ADR 0011, ADR 0013 §4) ------------ */

export function useCreateNextDraftRuleset() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { policyVersion: TaxPolicyVersionId; reason: string }) =>
      api.createNextDraftRuleset(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.rulesets.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.ruleDrafts.all });
    },
  });
}

export function usePolicyReadiness(
  rulesetId: string | undefined,
  policyVersion: TaxPolicyVersionId,
  options: { enabled?: boolean } = {},
) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.ruleDrafts.readiness(rulesetId ?? "", policyVersion),
    queryFn: ({ signal }) => api.getPolicyReadiness(rulesetId ?? "", policyVersion, signal),
    enabled: Boolean(rulesetId) && (options.enabled ?? true),
  });
}

export function useCreateIpiRestatements() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      rulesetId: string;
      body: Omit<IpiRestatementRequest, "schemaVersion">;
    }) => api.createIpiRestatements(input.rulesetId, input.body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.ruleDrafts.all });
    },
  });
}

export function useProposeRulesFromFacts() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (selector: FactProposalRequest["selector"]) => api.proposeRulesFromFacts(selector),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.ruleDrafts.all });
    },
  });
}

export function useRuleProposalDrafts(
  query: {
    rulesetId?: string;
    status?: RuleProposalDraftStatus[];
    origin?: RuleProposalDraftOrigin[];
    limit?: number;
  },
  options: { enabled?: boolean } = {},
) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.ruleDrafts.list(query),
    queryFn: ({ signal }) => api.listRuleProposalDrafts({ limit: 200, ...query }, signal),
    enabled: options.enabled ?? true,
  });
}

export function useSubmitRuleProposalDraft() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      draftId: string;
      body: Omit<RuleProposalDraftSubmit, "schemaVersion">;
      revision: number;
    }) => api.submitRuleProposalDraft(input.draftId, input.body, input.revision),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.ruleDrafts.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.proposals.all });
    },
  });
}

export function useDiscardRuleProposalDraft() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { draftId: string; reason: string; revision: number }) =>
      api.discardRuleProposalDraft(input.draftId, input.reason, input.revision),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.ruleDrafts.all });
    },
  });
}

/* -- Notas fiscais: NF-e imports and observed tax paths ----------------------- */

export function useNfeImports(cursor: string | null, options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.nfe.imports(cursor),
    queryFn: ({ signal }) => api.listNfeImports({ cursor, limit: 10 }, signal),
    enabled: options.enabled ?? true,
  });
}

export function useNfeImport(batchId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.nfe.import(batchId ?? "none"),
    queryFn: ({ signal }) => api.getNfeImport(batchId as string, signal),
    enabled: Boolean(batchId),
  });
}

export function useNfeObservedPaths(
  query: NfeObservedPathQuery & { cursor?: string | null; limit?: number },
  options: { enabled?: boolean } = {},
) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.nfe.paths(query),
    queryFn: ({ signal }) => api.listNfeObservedPaths(query, signal),
    enabled: options.enabled ?? true,
    placeholderData: (previous) => previous,
  });
}

export function useNfeObservedPath(pathId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.nfe.path(pathId ?? "none"),
    queryFn: ({ signal }) => api.getNfeObservedPath(pathId as string, signal),
    enabled: Boolean(pathId),
  });
}

/**
 * One upload request. The caller sequences the requests of a selection and
 * calls `useInvalidateNfe` once at the end, so the lists refresh once.
 */
export function useImportNfeFiles() {
  const api = useApi();
  return useMutation({
    mutationFn: (input: {
      files: NfeImportRequest["files"];
      idempotencyKey: string;
      signal?: AbortSignal;
    }) => api.importNfeFiles(input.files, input.idempotencyKey, input.signal),
  });
}

export function useInvalidateNfe() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.nfe.all });
  }, [queryClient]);
}

export function useCreateNfePathDraft() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { pathId: string; reason?: string; idempotencyKey: string }) =>
      api.createNfePathDraft(
        input.pathId,
        input.reason ? { reason: input.reason } : {},
        input.idempotencyKey,
      ),
    onSuccess: (_result, input) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.nfe.path(input.pathId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.ruleDrafts.all });
    },
  });
}

/* -- Notas fiscais workspace (tax managers): periods, findings, validation ----- */

/** The period tree: years, months, status chips and the dashboard figures. */
export function useNfePeriodTree(direction: NfeDirection | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.nfe.tree(direction),
    queryFn: ({ signal }) => api.getNfePeriodTree(direction, signal),
    placeholderData: (previous) => previous,
  });
}

export function useNfePeriod(period: string | undefined, direction: NfeDirection | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.nfe.period(period ?? "none", direction),
    queryFn: ({ signal }) => api.getNfePeriod(period as string, direction, signal),
    enabled: Boolean(period),
    placeholderData: (previous) => previous,
    // An AI run of the month finishes on its own: follow it until it settles.
    refetchInterval: (query) => {
      const status = query.state.data?.lastAiRun?.status;
      return status === "QUEUED" || status === "RUNNING" ? 2_000 : false;
    },
  });
}

export function useNfeMonthPaths(
  period: string | undefined,
  query: NfeMonthPathQuery & { cursor?: string | null; limit?: number },
) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.nfe.monthPaths(period ?? "none", query),
    queryFn: ({ signal }) => api.listNfeMonthPaths(period as string, query, signal),
    enabled: Boolean(period),
    placeholderData: (previous) => previous,
  });
}

export function useNfeMonthPath(
  period: string | undefined,
  pathId: string | undefined,
  direction: NfeDirection | undefined,
) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.nfe.monthPath(period ?? "none", pathId ?? "none", direction),
    queryFn: ({ signal }) =>
      api.getNfeMonthPath(period as string, pathId as string, direction, signal),
    enabled: Boolean(period && pathId),
  });
}

export function useNfeFindings(
  period: string | undefined,
  query: NfeFindingQuery & { cursor?: string | null; limit?: number },
  options: { enabled?: boolean } = {},
) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.nfe.findings(period ?? "none", query),
    queryFn: ({ signal }) => api.listNfeFindings(period as string, query, signal),
    enabled: Boolean(period) && (options.enabled ?? true),
    placeholderData: (previous) => previous,
  });
}

/** Audit trail of the month, newest first, "load more" by cursor. */
export function useNfeEvents(period: string | undefined) {
  const api = useApi();
  return useInfiniteQuery({
    queryKey: queryKeys.nfe.events(period ?? "none"),
    queryFn: ({ signal, pageParam }) =>
      api.listNfeEvents(period as string, { cursor: pageParam, limit: 25 }, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: Boolean(period),
  });
}

export function useNfeAnalysisRuns(period: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.nfe.runs(period ?? "none"),
    queryFn: ({ signal }) => api.listNfeAnalysisRuns(period as string, { limit: 10 }, signal),
    enabled: Boolean(period),
  });
}

export function useAcquisitionSettings(organizationId: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.acquisitionSettings(organizationId),
    queryFn: ({ signal }) => api.getAcquisitionSettings(signal, organizationId),
    enabled: Boolean(organizationId) && enabled,
    refetchOnWindowFocus: false,
  });
}

export function useUpdateAcquisitionSettings(organizationId: string) {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { update: AcquisitionSettingsUpdate; revision: number }) =>
      api.updateAcquisitionSettings(input.update, input.revision, organizationId),
    onSuccess: (settings) => {
      queryClient.setQueryData(queryKeys.acquisitionSettings(organizationId), settings);
    },
  });
}

export function useNfeWorkspaceSettings(options: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({
    queryKey: queryKeys.nfe.settings,
    queryFn: ({ signal }) => api.getNfeWorkspaceSettings(signal),
    enabled: options.enabled ?? true,
  });
}

function useInvalidateNfeWorkspace() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.nfe.workspace });
  }, [queryClient]);
}

export function useAcknowledgeNfe(period: string) {
  const api = useApi();
  const invalidate = useInvalidateNfeWorkspace();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      body: Omit<NfeAcknowledgementRequest, "schemaVersion">;
      idempotencyKey: string;
    }) => api.acknowledgeNfe(period, input.body, input.idempotencyKey),
    onSuccess: (result) => {
      invalidate();
      // A draft was created or reused: the drafts lists elsewhere are stale.
      if (result.draft) void queryClient.invalidateQueries({ queryKey: queryKeys.ruleDrafts.all });
    },
  });
}

export function useValidateNfePeriod(period: string) {
  const api = useApi();
  const invalidate = useInvalidateNfeWorkspace();
  return useMutation({
    mutationFn: (input: {
      body: Omit<NfeValidationRequest, "schemaVersion">;
      revision: number;
      idempotencyKey: string;
    }) => api.validateNfePeriod(period, input.body, input.revision, input.idempotencyKey),
    // A refused or stale transition still means the readiness changed.
    onSettled: invalidate,
  });
}

export function useRunNfeAnalysis(period: string) {
  const api = useApi();
  const invalidate = useInvalidateNfeWorkspace();
  return useMutation({
    mutationFn: (input: { kind: NfeAnalysisRequest["kind"]; idempotencyKey: string }) =>
      api.runNfeAnalysis(period, input.kind, input.idempotencyKey),
    onSettled: invalidate,
  });
}

export function useUpdateNfeWorkspaceSettings() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      update: Omit<NfeWorkspaceSettingsUpdate, "schemaVersion">;
      revision: number;
    }) => api.updateNfeWorkspaceSettings(input.update, input.revision),
    onSuccess: (settings) => {
      queryClient.setQueryData(queryKeys.nfe.settings, settings);
      // Availability of the AI lane travels with the tree and the month.
      void queryClient.invalidateQueries({ queryKey: queryKeys.nfe.workspace });
    },
  });
}
